import React, { useEffect, Fragment } from "react";
import { useAppContext } from "../../../libs/contextLib";
import { makeStyles } from "@material-ui/core/styles";
import { Button } from "@material-ui/core";
import { DataGrid } from "@material-ui/data-grid";
import { AffiliationService } from "../../../services/affiliation";
import Grid from "@material-ui/core/Grid";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import moment from "moment";
import ErrorLoadingPage from "../../errorPages/errorLoadingPage";
import AlertDialog from "../../helperComponents/alertDialog";
import Toast from "../../helperComponents/toast";
import AddEditAffiliationDialog from "./addEditAffiliationDialog";
import { Factory } from "../../../helpers/factories";
import DescriptionIcon from "@material-ui/icons/Description";
import SearchAffiliations from "../../helperComponents/searchAffiliations";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  dataGridRoot: {
    height: "100vh",
    width: "100%",
  },
  dataGrid: {
    backgroundColor: "white",
  },
}));

function Affiliations(props) {
  const classes = useStyles();
  const { currentUser } = useAppContext();

  //active affiliations
  const [affiliation, setAffiliation] = React.useState({
    _id: "",
    title: "",
    body: "",
    status: "",
    organisation: "",
    createdBy: "",
  });

  //loaded affiliations
  const [affiliations, setAffiliations] = React.useState([]);

  const [queryString, setQueryString] = React.useState([]);

  //current row selected
  const [selectedRowIndex, setSelectedRowIndex] = React.useState("");

  //operation type Save or Edit
  const [opType, setOpType] = React.useState("");

  //data is loading
  const [isDataLoading, setIsDataLoading] = React.useState(true);
  const [errorLoadingPage, setErrorLoadingPage] = React.useState(false);

  //affiliation dialog
  const [isAddEditAffiliationDialogOpen, setIsAddEditAffiliationDialogOpen] =
    React.useState(false);

  //toast options
  const [toastOptions, setToastOptions] = React.useState({
    open: false,
    message: "",
    duration: 6000,
  });

  //alert dialog
  const [alertDialogOptions, setAlertDialogOptions] = React.useState({
    open: false,
    title: "",
    message: "Delete affiliation?",
    cancelMessage: "Cancel",
    confirmMessage: "OK",
  });

  //rows for datagrid

  let rows = [];

  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    { field: "_id", headerName: "_id", width: 120, hide: "true" },
    { field: "schoolName", headerName: "School", width: 200 },
    { field: "schoolType", headerName: "Type", width: 120 },
    { field: "country", headerName: "Country", width: 130 },
    { field: "status", headerName: "Status", width: 120 },
    {
      field: "docURL",
      headerName: "Document",
      width: 120,
      renderCell: (params) => {
        return params.row.docURL ? (
          <Tooltip title="Download Affiliation Document">
            <IconButton
              aria-label="edit"
              onClick={() => {
                return downloadAffiliationDocument(params.row.docURL);
              }}
            >
              <DescriptionIcon />
            </IconButton>
          </Tooltip>
        ) : (
          "None"
        );
      },
    },
    {
      field: "dateCreated",
      headerName: "Date Created",
      width: 120,
      renderCell: (params) => {
        return moment(params.row.dateCreated).format("DD-MM-yyyy");
      },
    },
    {
      field: "",
      headerName: "",
      disableClickEventBubbling: true,
      renderCell: (params) => {
        const onClick = (op) => {
          setAffiliation(params.row);
          setSelectedRowIndex(params.row.id);
          op === "edit"
            ? handleEditAffiliation()
            : handleConfirmDeleteAffiliation();
          //return alert(JSON.stringify(thisRow, null, 4));
        };

        return (
          <div>
            <Tooltip title="Edit">
              <IconButton
                aria-label="edit"
                onClick={() => {
                  return onClick("edit");
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                aria-label="delete"
                onClick={() => {
                  return onClick("delete");
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  //pre-load verify token
  useEffect(() => {
    if (props.startFetchData) {
      onLoad();
    }
  }, [props.startFetchData]);

  useEffect(() => {
    onLoad();
  }, [queryString]);

  async function onLoad() {
    try {
      const affiliations = await AffiliationService.getAffiliations(
        queryString
      );
      setAffiliations(affiliations);
      setIsDataLoading(false);
    } catch (err) {
      setErrorLoadingPage(true);
    }
  }

  function loadRows(orgs) {
    let temp = [];
    affiliations.forEach((affiliation, index, orgs) => {
      affiliation["id"] = index;
      temp.push(affiliation);
    });
    return temp;
  }

  function handleCreateAffiliation() {
    setAffiliation(Factory.createAffiliation());
    setOpType("add");
    setIsAddEditAffiliationDialogOpen(true);
  }

  function handleCloseAddEditAffiliationDialog() {
    setIsAddEditAffiliationDialogOpen(false);
  }

  const handleEditAffiliation = () => {
    setOpType("edit");
    setIsAddEditAffiliationDialogOpen(true);
  };

  function handleOpenAlertDialog() {
    setAlertDialogOptions({ ...alertDialogOptions, open: true });
  }

  function handleCloseAlertDialog() {
    setAlertDialogOptions({ ...alertDialogOptions, open: false });
  }

  const downloadAffiliationDocument = async (docURL) => {
    setToastOptions({
      ...toastOptions,
      open: true,
      message: "Downloading...",
    });

    try {
      const url = await AffiliationService.downloadAffiliationDocument({
        docURL: docURL,
      });
      setToastOptions({
        ...toastOptions,
        open: true,
        message: "File Downloaded",
      });
    } catch (err) {
      setToastOptions({
        ...toastOptions,
        open: true,
        message: "Error dowloading file",
      });
    }
  };

  function handleConfirmDeleteAffiliation() {
    handleOpenAlertDialog();
  }

  async function handleProceedDeleteAffiliation() {
    setAlertDialogOptions({ ...alertDialogOptions, open: false });
    let temp = [...affiliations];
    temp.splice(selectedRowIndex, 1);
    try {
      await AffiliationService.deleteAffiliationById(affiliation._id);
      setAffiliations(temp);
      setToastOptions({
        ...toastOptions,
        open: true,
        message: "Affiliation Deleted",
      });
    } catch (err) {
      setToastOptions({
        ...toastOptions,
        open: true,
        message: err.message,
      });
    }
  }

  //toast
  const handleCloseToast = (e, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setToastOptions({ ...toastOptions, open: false });
  };

  //data grid
  function AffiliationDataGrid(props) {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12} className={classes.dataGridRoot}>
          <DataGrid
            className={classes.dataGrid}
            rows={rows}
            columns={columns}
            pageSize={10}
            autoHeight={true}
            loading={isDataLoading}
            {...props}
          />
        </Grid>
      </Grid>
    );
  }

  //assessment
  function AffiliationComponent(props) {
    return (
      <Fragment>
        <Toast
          toastOptions={toastOptions}
          handleCloseToast={handleCloseToast}
        />
        <AlertDialog
          alertDialogOptions={alertDialogOptions}
          handleClose={handleCloseAlertDialog}
          handleConfirm={handleProceedDeleteAffiliation}
        />
        <AddEditAffiliationDialog
          toastOptions={toastOptions}
          setToastOptions={setToastOptions}
          handleCloseToast={handleCloseToast}
          opType={opType}
          setOpType={setOpType}
          affiliation={affiliation}
          setAffiliation={setAffiliation}
          selectedRowIndex={selectedRowIndex}
          affiliations={affiliations}
          setAffiliations={setAffiliations}
          isAddEditAffiliationDialogOpen={isAddEditAffiliationDialogOpen}
          handleCloseAddEditAffiliationDialog={
            handleCloseAddEditAffiliationDialog
          }
        />
        <div className={classes.root}>
          <Grid container spacing={3}>
            {/* <Grid item xs={12}>
              <Typography variant="h4" className={classes.textCenter}>
                Affiliations
              </Typography>
            </Grid> */}

            <Grid
              item
              xs={12}
              sm={12}
              style={{ display: "flex", justifyContent: "flex-end" }}
            >
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleCreateAffiliation}
              >
                Add Affiliation
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <SearchAffiliations
                affiliation={affiliation}
                setAffiliation={setAffiliation}
                queryString={queryString}
                setQueryString={setQueryString}
              />
            </Grid>
            <Grid item xs={12}>
              {/* <OrganisationDialog
                isDialogOpen={isDialogOpen}
                organisation={organisation}
                setOrganisation={setOrganisation}
                organisations={organisations}
                setOrganisations={setOrganisations}
                selectedRowIndex={selectedRowIndex}
                setSelectedRowIndex={setSelectedRowIndex}
                handleCloseDialog={handleCloseDialog}
                opType={opType}
                setOpType={setOpType}
                toastOptions={toastOptions}
                setToastOptions={setToastOptions}
              /> */}
            </Grid>
            {AffiliationDataGrid()}
          </Grid>
        </div>
      </Fragment>
    );
  }

  if (!errorLoadingPage) {
    rows = loadRows(affiliations);
    return AffiliationComponent();
  } else {
    return <ErrorLoadingPage />;
  }

  // if (organisationsLoaded) {
  //   rows = loadRows(organisations);
  //   return AffiliationComponent({ loading: isDataLoading });
  // } else if (isDataLoading & !errorLoadingPage) {
  //   return AffiliationComponent({ loading: isDataLoading });
  // } else {
  //   return <ErrorLoadingPage />;
  // }
}

export default Affiliations;
